import { useEffect, useRef, useState } from "react";
import ClientAvatar from "@/components/ClientAvatar";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Calendar as CalendarIcon, Mail, Phone, Instagram, User, Camera, Clock, XCircle, DollarSign, Download, X, Gift, Trophy, CheckCircle, Ban, ChevronDown, ChevronUp, Pencil, Check, CalendarPlus, Trash2, Send, Loader2 } from "lucide-react";
import ManualDepositPopover from "@/components/admin/ManualDepositPopover";
import CollectPaymentDialog from "@/components/admin/CollectPaymentDialog";
import ClientEmailHistory from "@/components/admin/ClientEmailHistory";
import BlockClientCard from "@/components/admin/BlockClientCard";
import { Switch } from "@/components/ui/switch";
import { Calendar as DatePicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, isPast, isToday, parseISO } from "date-fns";
import { downloadClientICS } from "@/lib/calendar";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  booking_count_reset_at: string | null;
  instagram_link: string | null;
  instagram_approval_status: string | null;
  instagram_bypass_approval: boolean;
  referral_code: string | null;
  blocked_at: string | null;
  block_reason: string | null;
}

interface Booking {
  id: string;
  service_name: string;
  service_category: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  price: number;
  status: string;
  payment_status: string;
  payment_type: string | null;
  notes: string | null;
  created_at: string;
  discount_applied: number | null;
  checked_in_at: string | null;
  deposit_paid_amount?: number;
}

// Translate raw payment_status into a clear admin-facing label.
// "deposit_paid" or partial payments should read as "Deposit received".
const formatPaymentStatus = (b: Pick<Booking, "payment_status" | "payment_type" | "deposit_paid_amount" | "price">) => {
  const status = (b.payment_status || "pending").toLowerCase();
  const deposit = Number(b.deposit_paid_amount || 0);
  const price = Number(b.price || 0);

  if (status === "deposit_paid") return "Deposit received";
  if (status === "paid" && deposit > 0 && deposit < price) return "Deposit received";
  if (status === "paid") return "Paid in full";
  if (status === "refunded") return "Refunded";
  return "Pending";
};

interface Photo {
  id: string;
  file_path: string;
  category: string;
  created_at: string;
  booking_id: string | null;
  uploaded_by: string;
}

interface Coupon {
  id: string;
  code: string;
  discount_amount: number;
  is_used: boolean;
  reason: string;
  expires_at: string;
}

interface IgPost {
  id: string;
  post_url: string;
  status: string;
  created_at: string;
  criteria_status?: string;
  criteria_notes?: string;
}

const ClientDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [igPosts, setIgPosts] = useState<IgPost[]>([]);
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [newCouponAmount, setNewCouponAmount] = useState("5");
  const [newCouponReason, setNewCouponReason] = useState("admin_gift");
  const [newCouponCode, setNewCouponCode] = useState("");
  const [addingCoupon, setAddingCoupon] = useState(false);
  const [newCouponExpiry, setNewCouponExpiry] = useState<Date>(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 3); return d;
  });
  const [editingTimeId, setEditingTimeId] = useState<string | null>(null);
  const [editTimeValue, setEditTimeValue] = useState("");
  const [editingReferral, setEditingReferral] = useState(false);
  const [referralValue, setReferralValue] = useState("");
  const [savingReferral, setSavingReferral] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailToggles, setEmailToggles] = useState<Record<string, boolean>>({});
  const [busyPaymentId, setBusyPaymentId] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [paymentDialogBooking, setPaymentDialogBooking] = useState<Booking | null>(null);
  const [confirmedAddons, setConfirmedAddons] = useState<Record<string, { id: string; name: string; price: number; duration: string }[]>>({});
  // Section refs for scroll-to
  const bookingsRef = useRef<HTMLDivElement>(null);
  const cancellationsRef = useRef<HTMLDivElement>(null);
  const spentRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<HTMLDivElement>(null);
  const rewardsRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!userId) return;

    const fetchAll = async () => {
      const [profileRes, bookingsRes, photosRes, couponsRes, igPostsRes, referralRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", userId).single(),
        supabase.from("bookings").select("*").is("deleted_at", null).eq("user_id", userId).order("booking_date", { ascending: false }),
        supabase.from("client_photos").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
        supabase.from("coupons").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
        supabase.from("instagram_posts").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
        supabase.from("referral_entries").select("id").eq("referrer_user_id", userId),
      ]);

      setProfile(profileRes.data as Profile | null);
      const bookingsList = (bookingsRes.data as Booking[]) || [];
      setBookings(bookingsList);
      setPhotos((photosRes.data as Photo[]) || []);
      setIgPosts((igPostsRes.data as IgPost[]) || []);
      setReferralCount(referralRes.data?.length || 0);

      // Fetch confirmed add-ons for these bookings
      if (bookingsList.length > 0) {
        const { data: addonsData } = await supabase
          .from("booking_addons")
          .select("id, booking_id, name, price, duration, status")
          .in("booking_id", bookingsList.map((b) => b.id))
          .eq("status", "confirmed");
        const map: Record<string, { id: string; name: string; price: number; duration: string }[]> = {};
        (addonsData || []).forEach((a: any) => {
          if (!map[a.booking_id]) map[a.booking_id] = [];
          map[a.booking_id].push({ id: a.id, name: a.name, price: Number(a.price) || 0, duration: a.duration });
        });
        setConfirmedAddons(map);
      }

      // Auto-generate missing coupons for approved IG posts
      const approvedPosts = ((igPostsRes.data as IgPost[]) || []).filter(p => p.status === "approved");
      const existingCoupons = (couponsRes.data as Coupon[]) || [];
      const igCouponCount = existingCoupons.filter(c => c.reason === "instagram_share").length;

      if (approvedPosts.length > igCouponCount) {
        const missing = approvedPosts.length - igCouponCount;
        const inserts = [];
        const sixMonths = new Date();
        sixMonths.setMonth(sixMonths.getMonth() + 6);
        for (let i = 0; i < missing; i++) {
          const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
          inserts.push({
            user_id: userId,
            code,
            discount_amount: 5,
            reason: "instagram_share",
            expires_at: sixMonths.toISOString(),
          });
        }
        if (inserts.length > 0) {
          await supabase.from("coupons").insert(inserts);
          const { data: refreshed } = await supabase
            .from("coupons")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false });
          setCoupons((refreshed as Coupon[]) || []);
        } else {
          setCoupons(existingCoupons);
        }
      } else {
        setCoupons(existingCoupons);
      }

      setLoading(false);

      if (photosRes.data && photosRes.data.length > 0) {
        const urls: Record<string, string> = {};
        for (const photo of photosRes.data as Photo[]) {
          const { data } = await supabase.storage.from("client-photos").createSignedUrl(photo.file_path, 3600);
          if (data?.signedUrl) urls[photo.id] = data.signedUrl;
        }
        setPhotoUrls(urls);
      }
    };

    fetchAll();
  }, [userId]);

  const startEditTime = (b: Booking) => {
    // Convert "2:30 PM" to 24h "14:30" for input[type=time]
    const parsed = parseBookingTime(b.booking_time);
    if (parsed) {
      const hh = String(parsed.hours).padStart(2, "0");
      const mm = String(parsed.minutes).padStart(2, "0");
      setEditTimeValue(`${hh}:${mm}`);
    } else {
      setEditTimeValue("");
    }
    setEditingTimeId(b.id);
  };

  const saveEditTime = async (bookingId: string) => {
    if (!editTimeValue) return;
    // Convert 24h "14:30" back to "2:30 PM"
    const [hStr, mStr] = editTimeValue.split(":");
    let h = parseInt(hStr);
    const m = parseInt(mStr);
    const period = h >= 12 ? "PM" : "AM";
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    const formatted = `${h}:${String(m).padStart(2, "0")} ${period}`;

    const { error } = await supabase.from("bookings").update({ booking_time: formatted }).eq("id", bookingId);
    if (error) {
      toast.error("Failed to update time");
    } else {
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, booking_time: formatted } : b));
      toast.success("Appointment time updated");

      // Outlook calendar sync (update event)
      const updated = bookings.find((b) => b.id === bookingId);
      if (updated) {
        const { syncBookingToOutlook } = await import("@/lib/outlookSync");
        syncBookingToOutlook(
          {
            id: updated.id,
            service_name: updated.service_name,
            service_category: updated.service_category,
            booking_date: updated.booking_date,
            booking_time: formatted,
            duration: updated.duration,
            status: updated.status,
            notes: updated.notes,
            client_name: profile?.full_name || undefined,
            client_email: profile?.email || undefined,
            client_phone: profile?.phone || undefined,
          },
          "updated",
        ).catch(() => {});
      }
    }
    setEditingTimeId(null);
  };

  const handleDownloadPhoto = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  const handleDeleteClient = async () => {
    if (!userId) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-auth", {
        body: { action: "delete_client", target_user_id: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Client deleted successfully");
      navigate("/admin/clients");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete client");
    }
    setDeleting(false);
  };

  const togglePaymentStatus = async (b: Booking) => {
    setBusyPaymentId(b.id);
    const next = b.payment_status === "paid" ? "pending" : "paid";
    const { error } = await supabase
      .from("bookings")
      .update({ payment_status: next })
      .eq("id", b.id);
    setBusyPaymentId(null);
    if (error) {
      toast.error("Failed to update payment status");
      return;
    }
    setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, payment_status: next } : x)));
    toast.success(`Marked as ${next}`);
  };

  const sendConfirmationEmail = async (b: Booking) => {
    if (!profile?.email) {
      toast.error("Client has no email on file");
      return;
    }
    setSendingEmailId(b.id);
    try {
      const { error } = await supabase.functions.invoke("send-booking-email", {
        body: {
          type: "confirmation",
          email: profile.email,
          client_name: profile.full_name || undefined,
          service_name: b.service_name,
          service_category: b.service_category,
          booking_date: b.booking_date,
          booking_time: b.booking_time,
          duration: b.duration,
          price: b.price,
          discount_applied: b.discount_applied || 0,
          payment_type: b.payment_type || undefined,
        },
      });
      if (error) throw error;
      toast.success("Confirmation email sent");
    } catch (err: any) {
      toast.error(err.message || "Failed to send email");
    }
    setSendingEmailId(null);
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading client details…</div>;
  }

  if (!profile) {
    return <div className="p-6 text-muted-foreground">Client not found.</div>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingBookings = bookings.filter((b) => {
    const date = parseISO(b.booking_date);
    return (date >= today || isToday(date)) && b.status !== "cancelled" && b.status !== "completed";
  });

  const pastBookings = bookings.filter((b) => {
    const date = parseISO(b.booking_date);
    return (date < today && !isToday(date)) || b.status === "completed";
  });

  const cancelledBookings = bookings.filter((b) => b.status === "cancelled");

  const totalSpent = bookings
    .filter((b) => b.status !== "cancelled" && b.payment_status === "paid")
    .reduce((sum, b) => sum + (b.price - (b.discount_applied || 0)), 0);

  // Parse booking time like "2:30 PM" to hours/minutes
  const parseBookingTime = (timeStr: string) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return { hours, minutes };
  };

  // Calculate arrival diff in minutes for a booking
  const getArrivalDiff = (b: Booking): number | null => {
    if (!b.checked_in_at) return null;
    const checkedIn = new Date(b.checked_in_at);
    const parsed = parseBookingTime(b.booking_time);
    if (!parsed) return null;
    // Parse booking_date as local date and set the time
    const [year, month, day] = b.booking_date.split("-").map(Number);
    const scheduled = new Date(year, month - 1, day, parsed.hours, parsed.minutes, 0, 0);
    return (checkedIn.getTime() - scheduled.getTime()) / 60000;
  };

  // Calculate average arrival time from checked-in bookings
  const checkedInBookings = bookings.filter((b) => b.checked_in_at && b.status !== "cancelled");
  const arrivalDiffs = checkedInBookings.map(getArrivalDiff).filter((d): d is number => d !== null);
  const avgArrivalMinutes = arrivalDiffs.length > 0
    ? arrivalDiffs.reduce((sum, d) => sum + d, 0) / arrivalDiffs.length
    : null;

  const avgArrivalLabel = avgArrivalMinutes !== null
    ? avgArrivalMinutes > 1
      ? `${Math.round(avgArrivalMinutes)} min late`
      : avgArrivalMinutes < -1
        ? `${Math.abs(Math.round(avgArrivalMinutes))} min early`
        : "On time"
    : "N/A";

  // Parse duration strings to minutes
  const parseDurationToMinutes = (dur: string): number => {
    const lower = dur.toLowerCase().trim();
    let total = 0;
    const hrMatch = lower.match(/(\d+)\s*h/);
    const minMatch = lower.match(/(\d+)\s*m/);
    if (hrMatch) total += parseInt(hrMatch[1]) * 60;
    if (minMatch) total += parseInt(minMatch[1]);
    if (total === 0) { const num = parseInt(lower); if (!isNaN(num)) total = num; }
    return total;
  };
  const formatMins = (mins: number) => {
    if (mins >= 60) { const h = Math.floor(mins / 60); const m = Math.round(mins % 60); return m > 0 ? `${h}h ${m}m` : `${h}h`; }
    return `${Math.round(mins)}m`;
  };

  const clientDurations = bookings.filter(b => b.status !== "cancelled").map(b => parseDurationToMinutes(b.duration)).filter(d => d > 0);
  const clientAvgDuration = clientDurations.length > 0 ? clientDurations.reduce((a, b) => a + b, 0) / clientDurations.length : 0;

  const clientServiceTime: Record<string, { total: number; count: number }> = {};
  bookings.filter(b => b.status !== "cancelled").forEach(b => {
    const mins = parseDurationToMinutes(b.duration);
    if (mins <= 0) return;
    if (!clientServiceTime[b.service_name]) clientServiceTime[b.service_name] = { total: 0, count: 0 };
    clientServiceTime[b.service_name].total += mins;
    clientServiceTime[b.service_name].count += 1;
  });
  const clientServiceBreakdown = Object.entries(clientServiceTime)
    .map(([name, { total, count }]) => ({ name, avg: total / count, count }))
    .sort((a, b) => b.count - a.count);

  const completedBookings = bookings.filter(b => b.status === "completed").length;
  const noShowBookings = bookings.filter(b => b.status === "no_show").length;
  const avgSpentPerVisit = completedBookings > 0 ? totalSpent / completedBookings : 0;
  const mostBookedService = bookings
    .filter(b => b.status !== "cancelled")
    .reduce<Record<string, number>>((acc, b) => {
      acc[b.service_name] = (acc[b.service_name] || 0) + 1;
      return acc;
    }, {});
  const topService = Object.entries(mostBookedService).sort((a, b) => b[1] - a[1])[0];

  const photosByCategory = photos.reduce<Record<string, Photo[]>>((acc, p) => {
    acc[p.category] = acc[p.category] || [];
    acc[p.category].push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/clients")}>
            <ArrowLeft size={20} />
          </Button>
          <ClientAvatar userId={userId!} name={profile.full_name} size="xl" />
          <div>
            <h1 className="font-serif text-3xl font-bold italic">{profile.full_name || "Unnamed Client"}</h1>
            <p className="text-muted-foreground text-sm">Client since {format(parseISO(profile.created_at), "MMMM d, yyyy")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const eligible = bookings.filter((b) => b.status !== "cancelled");
              if (eligible.length === 0) {
                toast.info("No bookings to export.");
                return;
              }
              downloadClientICS(profile.full_name || "Client", eligible);
              toast.success(`Exported ${eligible.length} booking${eligible.length === 1 ? "" : "s"}`);
            }}
            className="gap-2"
            title="Download all bookings as a .ics calendar file (works with Outlook, Apple Calendar, Google)"
          >
            <Download size={16} />
            Export .ics
          </Button>
          <Button
            onClick={() => navigate(`/book?clientId=${userId}&clientName=${encodeURIComponent(profile.full_name || "")}`)}
            className="gap-2"
          >
            <CalendarPlus size={16} />
            Book Appointment
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon" className="text-destructive border-destructive/30 hover:bg-destructive/10">
                <Trash2 size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Client</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete <strong>{profile.full_name || "this client"}</strong> and all their bookings, photos, coupons, and rewards. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteClient}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? "Deleting…" : "Delete Client"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Quick Stats - Expandable */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all" onClick={() => scrollTo(bookingsRef)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <CalendarIcon size={18} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{bookings.filter(b => b.status !== "cancelled").length}</p>
                <p className="text-xs text-muted-foreground">Total Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card className={cn("cursor-pointer hover:ring-2 transition-all", avgArrivalMinutes !== null && avgArrivalMinutes > 10 ? "hover:ring-destructive/30 ring-1 ring-destructive/20 bg-destructive/5" : "hover:ring-primary/30")} onClick={() => scrollTo(bookingsRef)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={cn("h-10 w-10 rounded-full flex items-center justify-center", avgArrivalMinutes !== null && avgArrivalMinutes > 10 ? "bg-destructive/10" : "bg-primary/10")}>
                <Clock size={18} className={avgArrivalMinutes !== null && avgArrivalMinutes > 10 ? "text-destructive" : "text-primary"} />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", avgArrivalMinutes !== null && avgArrivalMinutes > 10 ? "text-destructive" : avgArrivalMinutes !== null && avgArrivalMinutes > 0 ? "text-destructive/70" : avgArrivalMinutes !== null && avgArrivalMinutes < 0 ? "text-primary" : "")}>
                  {avgArrivalLabel}
                </p>
                <p className="text-xs text-muted-foreground">Avg Arrival</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:ring-2 hover:ring-destructive/30 transition-all" onClick={() => scrollTo(cancellationsRef)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle size={18} className="text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cancelledBookings.length}</p>
                <p className="text-xs text-muted-foreground">Cancellations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:ring-2 hover:ring-accent/50 transition-all" onClick={() => scrollTo(photosRef)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-accent/50 flex items-center justify-center">
                <Camera size={18} className="text-accent-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{photos.length}</p>
                <p className="text-xs text-muted-foreground">Photos</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all" onClick={() => scrollTo(rewardsRef)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Trophy size={18} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{igPosts.filter(p => p.status === "approved").length + referralCount}</p>
                <p className="text-xs text-muted-foreground">Rewards</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Expand/Collapse Button */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground gap-1"
          onClick={() => setStatsExpanded(!statsExpanded)}
        >
          {statsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {statsExpanded ? "Hide Details" : "Show More Details"}
        </Button>

        {/* Expanded Details */}
        {statsExpanded && (
          <>
          <Card>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-1">Completed</p>
                <p className="font-semibold text-lg">{completedBookings}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">No Shows</p>
                <p className="font-semibold text-lg">{noShowBookings}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Total Spent</p>
                <p className="font-semibold text-lg">${totalSpent.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Avg per Visit</p>
                <p className="font-semibold text-lg">${avgSpentPerVisit.toFixed(0)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Check-ins Recorded</p>
                <p className="font-semibold text-lg">{checkedInBookings.length}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Top Service</p>
                <p className="font-semibold text-lg truncate">{topService ? topService[0] : "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Active Coupons</p>
                <p className="font-semibold text-lg">{coupons.filter(c => !c.is_used).length}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Referrals</p>
                <p className="font-semibold text-lg">{referralCount}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Avg Appt Time</p>
                <p className="font-semibold text-lg">{clientAvgDuration > 0 ? formatMins(clientAvgDuration) : "—"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Per-service time breakdown */}
          {clientServiceBreakdown.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Clock size={16} /> Time Breakdown by Service</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {clientServiceBreakdown.map((s) => (
                  <div key={s.name} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/30">
                    <div>
                      <p className="text-sm font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.count} appt{s.count !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{formatMins(s.avg)}</p>
                      <p className="text-[10px] text-muted-foreground">avg</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          </>
        )}
      </div>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><User size={18} /> Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-muted-foreground" />
              <span>{profile.email || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={14} className="text-muted-foreground" />
              <span>{profile.phone || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Instagram size={14} className="text-muted-foreground" />
              {profile.instagram_link ? (
                <a href={profile.instagram_link} target="_blank" rel="noopener noreferrer" className="text-primary underline truncate">
                  {profile.instagram_link}
                </a>
              ) : (
                <span>—</span>
              )}
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Referral Code:</span>
            {editingReferral ? (
              <div className="flex items-center gap-1">
                <Input
                  value={referralValue}
                  onChange={(e) => setReferralValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12))}
                  placeholder="CODE"
                  className="h-7 w-32 text-xs font-mono"
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  disabled={savingReferral}
                  onClick={async () => {
                    const newCode = referralValue.trim() || null;
                    if (newCode === (profile.referral_code || null)) {
                      setEditingReferral(false);
                      return;
                    }
                    if (newCode && newCode.length < 4) {
                      toast.error("Referral code must be at least 4 characters");
                      return;
                    }
                    setSavingReferral(true);
                    if (newCode) {
                      const { data: existing } = await supabase
                        .from("profiles")
                        .select("user_id")
                        .eq("referral_code", newCode)
                        .neq("user_id", profile.user_id)
                        .maybeSingle();
                      if (existing) {
                        setSavingReferral(false);
                        toast.error("That referral code is already in use");
                        return;
                      }
                    }
                    const { error } = await supabase
                      .from("profiles")
                      .update({ referral_code: newCode })
                      .eq("user_id", profile.user_id);
                    setSavingReferral(false);
                    if (error) {
                      toast.error("Failed to update referral code", { description: error.message });
                      return;
                    }
                    setProfile((p) => (p ? { ...p, referral_code: newCode } : p));
                    setEditingReferral(false);
                    toast.success("Referral code updated");
                  }}
                >
                  {savingReferral ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} className="text-primary" />}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  disabled={savingReferral}
                  onClick={() => setEditingReferral(false)}
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <>
                <span className="font-mono text-foreground">{profile.referral_code || "—"}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5"
                  onClick={() => {
                    setReferralValue(profile.referral_code || "");
                    setEditingReferral(true);
                  }}
                >
                  <Pencil size={10} />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coupons */}
      {coupons.length > 0 && (
        <Card ref={spentRef}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><DollarSign size={18} /> Coupons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {coupons.map((c) => (
                <Badge key={c.id} variant={c.is_used ? "secondary" : "default"} className="text-xs py-1 px-2.5">
                  {c.code} — ${c.discount_amount} off
                  {c.is_used && " (used)"}
                  {!c.is_used && isPast(parseISO(c.expires_at)) && " (expired)"}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email History */}
      <ClientEmailHistory userId={profile.user_id} email={profile.email} />

      {/* Upcoming Appointments */}
      <Card ref={bookingsRef}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock size={18} /> Upcoming Appointments ({upcomingBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming appointments.</p>
          ) : (
            <div className="space-y-3">
              {upcomingBookings.map((b) => (
                <div key={b.id} className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{b.service_name}</p>
                    {editingTimeId === b.id ? (
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          type="time"
                          value={editTimeValue}
                          onChange={(e) => setEditTimeValue(e.target.value)}
                          className="h-7 w-28 text-xs"
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => saveEditTime(b.id)}>
                          <Check size={14} className="text-primary" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingTimeId(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {format(parseISO(b.booking_date), "MMM d, yyyy")} at {b.booking_time} · {b.duration}
                        <Button size="icon" variant="ghost" className="h-5 w-5 ml-1" onClick={() => startEditTime(b)}>
                          <Pencil size={10} />
                        </Button>
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                      Booked {format(new Date(b.created_at), "EEE, MMM d, yyyy 'at' h:mm a")}
                    </p>
                    {b.checked_in_at && (() => {
                      const diff = getArrivalDiff(b);
                      if (diff === null) return null;
                      const label = diff > 1 ? `${Math.round(diff)} min late` : diff < -1 ? `${Math.abs(Math.round(diff))} min early` : "On time";
                      return (
                        <p className={cn("text-xs mt-0.5 font-medium", diff > 10 ? "text-destructive" : diff > 0 ? "text-destructive/70" : "text-primary")}>
                          ⏱ Arrived: {format(new Date(b.checked_in_at), "h:mm a")} ({label})
                        </p>
                      );
                    })()}
                  </div>
                  <div className="text-right space-y-1">
                    <p className="text-sm font-bold">${b.price}</p>
                    <button
                      type="button"
                      onClick={() => setPaymentDialogBooking(b)}
                      className="inline-flex"
                      title="Open payment collection"
                    >
                      <Badge
                        variant={b.payment_status === "paid" && Number(b.deposit_paid_amount || 0) >= Number(b.price || 0) ? "default" : "secondary"}
                        className="text-[10px] cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        {formatPaymentStatus(b)}
                      </Badge>
                    </button>

                    {profile?.email && (
                      <div className="flex items-center justify-end gap-1.5 pt-1">
                        <Switch
                          checked={!!emailToggles[b.id]}
                          onCheckedChange={(v) => setEmailToggles((prev) => ({ ...prev, [b.id]: v }))}
                          className="scale-75"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px]"
                          disabled={!emailToggles[b.id] || sendingEmailId === b.id}
                          onClick={() => sendConfirmationEmail(b)}
                        >
                          {sendingEmailId === b.id ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <><Send size={10} className="mr-1" /> Email</>
                          )}
                        </Button>
                      </div>
                    )}
                    </div>
                  </div>
                  {(confirmedAddons[b.id]?.length ?? 0) > 0 && (
                    <div className="mt-3 pt-3 border-t border-border/40 space-y-1">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Add-ons</p>
                      {confirmedAddons[b.id].map((a) => (
                        <div key={a.id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">+ {a.name} <span className="text-muted-foreground/60">· {a.duration}</span></span>
                          <span className="font-medium">${a.price.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-border/30 mt-1">
                        <span className="font-semibold">Appointment total</span>
                        <span className="font-bold">${Number(b.price).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Appointments / Cancellations */}
      <Card ref={cancellationsRef}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarIcon size={18} /> Past Appointments ({pastBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pastBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No past appointments.</p>
          ) : (
            <div className="space-y-2">
              {pastBookings.slice(0, 20).map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/20">
                  <div>
                    <p className="font-medium text-sm">{b.service_name}</p>
                    {editingTimeId === b.id ? (
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          type="time"
                          value={editTimeValue}
                          onChange={(e) => setEditTimeValue(e.target.value)}
                          className="h-7 w-28 text-xs"
                        />
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => saveEditTime(b.id)}>
                          <Check size={14} className="text-primary" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingTimeId(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        {format(parseISO(b.booking_date), "MMM d, yyyy")} at {b.booking_time}
                        <Button size="icon" variant="ghost" className="h-5 w-5 ml-1" onClick={() => startEditTime(b)}>
                          <Pencil size={10} />
                        </Button>
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                      Booked {format(new Date(b.created_at), "EEE, MMM d, yyyy 'at' h:mm a")}
                    </p>
                    {b.checked_in_at && (() => {
                      const diff = getArrivalDiff(b);
                      if (diff === null) return null;
                      const label = diff > 1 ? `${Math.round(diff)} min late` : diff < -1 ? `${Math.abs(Math.round(diff))} min early` : "On time";
                      return (
                        <p className={cn("text-xs mt-0.5 font-medium", diff > 10 ? "text-destructive" : diff > 0 ? "text-destructive/70" : "text-primary")}>
                          ⏱ Arrived: {format(new Date(b.checked_in_at), "h:mm a")} ({label})
                        </p>
                      );
                    })()}
                    {b.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">"{b.notes}"</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">
                      ${b.price}
                      {(b.discount_applied || 0) > 0 && (
                        <span className="text-xs text-green-500 ml-1">(-${b.discount_applied})</span>
                      )}
                    </p>
                    {b.status === "cancelled" ? (
                      <Badge variant="destructive" className="text-[10px]">cancelled</Badge>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPaymentDialogBooking(b)}
                        className="inline-flex"
                        title="Open payment collection"
                      >
                        <Badge
                          variant={b.payment_status === "paid" && Number(b.deposit_paid_amount || 0) >= Number(b.price || 0) ? "default" : "secondary"}
                          className="text-[10px] cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          {formatPaymentStatus(b)}
                        </Badge>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {pastBookings.length > 20 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  Showing 20 of {pastBookings.length} past appointments
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rewards & IG Posts */}
      <Card ref={rewardsRef}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy size={18} /> Rewards
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary: Earned / Spent / Balance */}
          {(() => {
            const approvedPosts = igPosts.filter(p => p.status === "approved").length;
            const earned = approvedPosts + referralCount;
            const usedCouponsCount = coupons.filter(c => c.is_used).length;
            const usedCouponsValue = coupons.filter(c => c.is_used).reduce((sum, c) => sum + c.discount_amount, 0);
            const earnedValue = coupons.reduce((sum, c) => sum + c.discount_amount, 0);
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 border border-primary/20 bg-primary/5 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Rewards</p>
                  <p className="text-xl font-bold text-primary">{earned}</p>
                </div>
                <div className="p-3 border border-border/30 bg-muted/20 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Earned</p>
                  <p className="text-xl font-bold">${earnedValue}</p>
                  <p className="text-[10px] text-muted-foreground">{referralCount} referral{referralCount !== 1 ? "s" : ""} · {approvedPosts} post{approvedPosts !== 1 ? "s" : ""}</p>
                </div>
                <div className="p-3 border border-border/30 bg-muted/20 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Spent</p>
                  <p className="text-xl font-bold">${usedCouponsValue}</p>
                  <p className="text-[10px] text-muted-foreground">{usedCouponsCount} coupon{usedCouponsCount !== 1 ? "s" : ""} used</p>
                </div>
                <div className="p-3 border border-border/30 bg-muted/20 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Available</p>
                  <p className="text-xl font-bold">${earnedValue - usedCouponsValue}</p>
                  <p className="text-[10px] text-muted-foreground">{coupons.filter(c => !c.is_used).length} active coupon{coupons.filter(c => !c.is_used).length !== 1 ? "s" : ""}</p>
                </div>
              </div>
            );
          })()}

          {/* Referrals Detail */}
          <div className="border border-border/30 rounded-lg p-4 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
              <Gift size={14} /> Referrals ({referralCount})
            </p>
            {referralCount === 0 ? (
              <p className="text-xs text-muted-foreground">No referrals yet.</p>
            ) : (
              <p className="text-sm">{referralCount} friend{referralCount !== 1 ? "s" : ""} referred · Code: <span className="font-mono font-bold">{profile.referral_code || "—"}</span></p>
            )}
          </div>

          {/* Coupons Detail */}
          <div className="border border-border/30 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <DollarSign size={14} /> Coupons ({coupons.length})
              </p>
              <Button
                size="sm"
                variant="outline"
                className="text-[10px] gap-1 h-6 px-2 border-primary/30 hover:bg-primary/10 tracking-widest uppercase"
                onClick={() => {
                  setNewCouponCode("GIFT-" + Math.random().toString(36).substring(2, 8).toUpperCase());
                  setNewCouponAmount("5");
                  setNewCouponReason("admin_gift");
                  const defaultExpiry = new Date();
                  defaultExpiry.setMonth(defaultExpiry.getMonth() + 3);
                  setNewCouponExpiry(defaultExpiry);
                  setShowAddCoupon(true);
                }}
              >
                <Gift size={10} /> Add Coupon
              </Button>
            </div>

            {/* Add Coupon Form */}
            {showAddCoupon && (
              <div className="border border-primary/20 bg-primary/5 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs tracking-wide uppercase text-muted-foreground">Code</label>
                    <Input
                      value={newCouponCode}
                      onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                      placeholder="GIFT-XXXXXX"
                      className="bg-background border-border/50 mt-1 font-mono text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-wide uppercase text-muted-foreground">Amount ($)</label>
                    <Input
                      type="number"
                      min="1"
                      value={newCouponAmount}
                      onChange={(e) => setNewCouponAmount(e.target.value)}
                      className="bg-background border-border/50 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs tracking-wide uppercase text-muted-foreground">Reason</label>
                  <select
                    value={newCouponReason}
                    onChange={(e) => setNewCouponReason(e.target.value)}
                    className="w-full mt-1 bg-background border border-border/50 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="admin_gift">Admin Gift</option>
                    <option value="loyalty">Loyalty Reward</option>
                    <option value="compensation">Compensation</option>
                    <option value="promotion">Promotion</option>
                    <option value="instagram_share">Instagram Share</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs tracking-wide uppercase text-muted-foreground">Expires</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal text-sm bg-background border-border/50">
                        <CalendarIcon size={14} className="mr-2 text-muted-foreground" />
                        {format(newCouponExpiry, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <DatePicker
                        mode="single"
                        selected={newCouponExpiry}
                        onSelect={(d) => d && setNewCouponExpiry(d)}
                        disabled={(date) => date < new Date()}
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowAddCoupon(false)} className="text-xs">
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={addingCoupon || !newCouponCode || !newCouponAmount}
                    className="text-xs tracking-widest uppercase"
                    onClick={async () => {
                      setAddingCoupon(true);
                      try {
                        const { error } = await supabase.from("coupons").insert({
                          user_id: userId!,
                          code: newCouponCode,
                          discount_amount: Number(newCouponAmount),
                          reason: newCouponReason,
                          expires_at: newCouponExpiry.toISOString(),
                        });
                        if (error) throw error;
                        const { data: refreshed } = await supabase
                          .from("coupons")
                          .select("*")
                          .eq("user_id", userId!)
                          .order("created_at", { ascending: false });
                        setCoupons((refreshed as Coupon[]) || []);
                        setShowAddCoupon(false);
                        toast.success(`Coupon ${newCouponCode} added!`);
                        // Send credit email
                        if (profile?.email) {
                          supabase.functions.invoke("send-credit-email", {
                            body: { email: profile.email, client_name: profile.full_name, coupon_code: newCouponCode, discount_amount: Number(newCouponAmount), expires_at: newCouponExpiry.toISOString(), reason: newCouponReason },
                          }).catch(() => {});
                        }
                      } catch (err: any) {
                        toast.error(err.message || "Failed to add coupon");
                      }
                      setAddingCoupon(false);
                    }}
                  >
                    {addingCoupon ? "Adding…" : "Add Coupon"}
                  </Button>
                </div>
              </div>
            )}

            {coupons.length > 0 ? (
              <div className="space-y-1.5">
                {coupons.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/20">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold">{c.code}</span>
                      <Badge variant={c.is_used ? "secondary" : "default"} className="text-[10px]">
                        {c.is_used ? "Used" : isPast(parseISO(c.expires_at)) ? "Expired" : "Active"}
                      </Badge>
                    </div>
                    <span className="text-sm font-medium">${c.discount_amount} off</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No coupons yet.</p>
            )}
          </div>

          {igPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No Instagram posts submitted.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pending Posts */}
              <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-yellow-600 flex items-center gap-1.5">
                  <Clock size={14} />
                  Pending Review ({igPosts.filter(p => p.status === "pending").length})
                </p>
                {igPosts.filter(p => p.status === "pending").length === 0 ? (
                  <p className="text-xs text-muted-foreground">No posts awaiting review.</p>
                ) : (
                  <div className="space-y-2">
                    {igPosts.filter(p => p.status === "pending").map((post) => (
                      <div key={post.id} className="p-2.5 border border-border/30 bg-background rounded-lg space-y-2">
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline truncate block">
                          {post.post_url}
                        </a>
                        {/* Criteria Status */}
                        <div className="flex items-center gap-1.5">
                          <Badge
                            variant={post.criteria_status === "meets_criteria" ? "default" : post.criteria_status === "unchecked" ? "secondary" : "destructive"}
                            className="text-[9px]"
                          >
                            {post.criteria_status === "meets_criteria" ? "✓ Meets Criteria"
                              : post.criteria_status === "missing_tag" ? "Missing @tag"
                              : post.criteria_status === "missing_hashtag" ? "Missing hashtag"
                              : post.criteria_status === "missing_both" ? "Missing tag & hashtag"
                              : "Unchecked"}
                          </Badge>
                          {post.criteria_notes && (
                            <span className="text-[9px] text-muted-foreground italic">{post.criteria_notes}</span>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">{format(parseISO(post.created_at), "MMM d, yyyy")}</span>
                          <div className="flex gap-1.5 flex-wrap">
                            {/* Criteria check buttons */}
                            <Select
                              value={post.criteria_status || "unchecked"}
                              onValueChange={async (val) => {
                                await supabase.from("instagram_posts").update({ criteria_status: val } as any).eq("id", post.id);
                                setIgPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, criteria_status: val } : p));
                              }}
                            >
                              <SelectTrigger className="h-6 text-[10px] w-[110px] px-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unchecked">Unchecked</SelectItem>
                                <SelectItem value="meets_criteria">Meets Criteria</SelectItem>
                                <SelectItem value="missing_tag">Missing @tag</SelectItem>
                                <SelectItem value="missing_hashtag">Missing Hashtag</SelectItem>
                                <SelectItem value="missing_both">Missing Both</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] gap-1 h-6 px-2 border-green-500/30 text-green-700 hover:bg-green-500/10"
                              onClick={async () => {
                                await supabase.from("instagram_posts").update({ status: "approved" }).eq("id", post.id);
                                setIgPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, status: "approved" } : p));
                                // Generate coupon for this approved post
                                const { data: existingCoupon } = await supabase
                                  .from("coupons")
                                  .select("id")
                                  .eq("user_id", userId!)
                                  .eq("reason", "instagram_share")
                                  .eq("booking_id", post.id)
                                  .maybeSingle();
                                if (!existingCoupon) {
                                  const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
                                  const igExpiry = new Date();
                                  igExpiry.setMonth(igExpiry.getMonth() + 6);
                                  await supabase.from("coupons").insert({
                                    user_id: userId!,
                                    code,
                                    discount_amount: 5,
                                    reason: "instagram_share",
                                    booking_id: null,
                                    expires_at: igExpiry.toISOString(),
                                  });
                                  if (profile?.email) {
                                    supabase.functions.invoke("send-credit-email", {
                                      body: { email: profile.email, client_name: profile.full_name, coupon_code: code, discount_amount: 5, expires_at: igExpiry.toISOString(), reason: "instagram_share" },
                                    }).catch(() => {});
                                  }
                                  const { data: newCoupons } = await supabase
                                    .from("coupons")
                                    .select("*")
                                    .eq("user_id", userId!)
                                    .order("created_at", { ascending: false });
                                  setCoupons((newCoupons as Coupon[]) || []);
                                }
                              }}
                            >
                              <CheckCircle size={10} /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] gap-1 h-6 px-2 border-destructive/30 text-destructive hover:bg-destructive/10"
                              onClick={async () => {
                                await supabase.from("instagram_posts").update({ status: "rejected" }).eq("id", post.id);
                                setIgPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, status: "rejected" } : p));
                              }}
                            >
                              <Ban size={10} /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Approved Posts */}
              <div className="border border-green-500/30 bg-green-500/5 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-green-600 flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Approved ({igPosts.filter(p => p.status === "approved").length})
                </p>
                {igPosts.filter(p => p.status === "approved").length === 0 ? (
                  <p className="text-xs text-muted-foreground">No approved posts yet.</p>
                ) : (
                  <div className="space-y-2">
                    {igPosts.filter(p => p.status === "approved").map((post) => (
                      <div key={post.id} className="p-2.5 border border-border/30 bg-background rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline truncate">
                            {post.post_url}
                          </a>
                          <span className="text-[10px] text-muted-foreground shrink-0">{format(parseISO(post.created_at), "MMM d")}</span>
                        </div>
                        <Badge
                          variant={post.criteria_status === "meets_criteria" ? "default" : post.criteria_status === "unchecked" ? "secondary" : "destructive"}
                          className="text-[9px]"
                        >
                          {post.criteria_status === "meets_criteria" ? "✓ Meets Criteria"
                            : post.criteria_status === "missing_tag" ? "Missing @tag"
                            : post.criteria_status === "missing_hashtag" ? "Missing hashtag"
                            : post.criteria_status === "missing_both" ? "Missing tag & hashtag"
                            : "Unchecked"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card ref={photosRef}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Camera size={18} /> Client Photos ({photos.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? (
            <p className="text-sm text-muted-foreground">No photos uploaded.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(photosByCategory).map(([category, categoryPhotos]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold capitalize mb-2">{category}</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {categoryPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="aspect-square rounded-lg overflow-hidden bg-muted border border-border/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                        onClick={() => photoUrls[photo.id] && setLightboxPhoto(photo.id)}
                      >
                        {photoUrls[photo.id] ? (
                          <img
                            src={photoUrls[photo.id]}
                            alt={`${category} photo`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Camera size={16} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block / Unblock client */}
      <BlockClientCard
        profile={profile}
        onChange={(blocked_at, block_reason) =>
          setProfile((p) => (p ? { ...p, blocked_at, block_reason } : p))
        }
      />

      {/* Lightbox Dialog */}
      <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-none">
          {lightboxPhoto && photoUrls[lightboxPhoto] && (
            <div className="relative flex flex-col items-center gap-3">
              <img
                src={photoUrls[lightboxPhoto]}
                alt="Client photo"
                className="max-h-[80vh] w-auto rounded-lg object-contain"
              />
              <Button
                variant="secondary"
                size="sm"
                className="gap-2"
                onClick={() => {
                  const photo = photos.find(p => p.id === lightboxPhoto);
                  handleDownloadPhoto(
                    photoUrls[lightboxPhoto],
                    photo ? `${photo.category}-${format(parseISO(photo.created_at), "yyyy-MM-dd")}.jpg` : "photo.jpg"
                  );
                }}
              >
                <Download size={14} />
                Download
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CollectPaymentDialog
        open={!!paymentDialogBooking}
        onOpenChange={(o) => { if (!o) setPaymentDialogBooking(null); }}
        booking={paymentDialogBooking ? {
          id: paymentDialogBooking.id,
          user_id: userId!,
          service_name: paymentDialogBooking.service_name,
          service_category: paymentDialogBooking.service_category,
          booking_date: paymentDialogBooking.booking_date,
          booking_time: paymentDialogBooking.booking_time,
          duration: paymentDialogBooking.duration,
          price: paymentDialogBooking.price,
          discount_applied: paymentDialogBooking.discount_applied,
          deposit_paid_amount: 0,
        } : null}
        clientName={profile?.full_name}
        showCompleteToggle={false}
        onSaved={async () => {
          const { data } = await supabase.from("bookings").select("*").is("deleted_at", null).eq("user_id", userId!).order("booking_date", { ascending: false });
          setBookings((data as Booking[]) || []);
        }}
      />
    </div>
  );
};

export default ClientDetail;
